"use client";

import * as React from "react";
import { motion } from "motion/react";
import { cn } from "../lib/cn";
import { durations, easings } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * AuthCard - the branded card the auth route renders inside `AuthShell`.
 *
 * The `max-w-[460px] bg-surface border border-hairline rounded-2xl shadow-lift`
 * card + logo header that the unified AuthScreen / ForgotPassword /
 * ResetPassword each hand-built (and ResetPassword half-extracted into a local
 * `Shell`). Re-expressed in brand tokens (`bg-card`, `border-border`,
 * `shadow-lift`). The consumer fills the body with the auth slot atoms
 * (`AuthFieldRow`, `PasswordInput`, `AuthErrorAlert`, `AuthBadge`,
 * `PasswordRequirements`) plus `OAuthProviderButtons` / `SuccessState`.
 *
 * Sets the `.gs-card` hook automatically so `AuthBackground` can lock its
 * logo-mask diamond onto the card and follow it as the body grows.
 *
 * Motion: mounts with `transitions.surface` (opacity + a small `y` lift),
 * replacing the unified `fadeIn 0.22s`. Honors `prefers-reduced-motion` via
 * `useReducedMotion()` - reduced motion mounts at the final state with no
 * spring. The stage's own fade lives on `AuthShell`.
 *
 *   <AuthShell>
 *     <AuthCard logoSrc="/logo-blue.png">
 *       <h2>Sign in to your account</h2>
 *       ...
 *     </AuthCard>
 *   </AuthShell>
 */
export interface AuthCardProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag"
  > {
  /** Logo shown in the card header. Omit (or pass `null`) to hide the header. */
  logoSrc?: string | null;
  /** Alt text for the logo. Default "AISquare". */
  logoAlt?: string;
  /** Max card width in px. Default 460 (the unified value). */
  maxWidth?: number;
  /** Animate the card-mount spring. Default true; reduced-motion forces static. */
  animate?: boolean;
}

export const AuthCard = React.forwardRef<HTMLDivElement, AuthCardProps>(
  (
    {
      className,
      logoSrc = "/logo-blue.png",
      logoAlt = "AISquare",
      maxWidth = 460,
      animate = true,
      children,
      style,
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    // Treat `null` (pre-resolve) as "motion allowed" so SSR content does not
    // animate-then-snap once the client resolves.
    const motionAllowed = reduced === false || reduced === null;
    const animated = animate && motionAllowed;

    // `.gs-card` is the hook AuthBackground queries to size + centre its mask.
    const cardClass = cn(
      "gs-card w-full rounded-2xl border border-border bg-card px-7 pb-6 pt-[26px] shadow-lift",
      className,
    );
    const cardStyle: React.CSSProperties = { maxWidth, ...style };

    const header = logoSrc ? (
      <div className="mb-5 flex items-center gap-[9px] text-base italic text-foreground">
        <img className="block h-[21px] w-auto" src={logoSrc} alt={logoAlt} />
      </div>
    ) : null;

    if (animated) {
      return (
        <motion.div
          ref={ref}
          data-slot="auth-card"
          className={cardClass}
          style={cardStyle}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: durations.surface, ease: easings.smoothOut }}
        >
          {header}
          {children}
        </motion.div>
      );
    }

    return (
      <div ref={ref} data-slot="auth-card" className={cardClass} style={cardStyle} {...props}>
        {header}
        {children}
      </div>
    );
  },
);
AuthCard.displayName = "AuthCard";
